import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from './../../config/axios';

export const getCoins = createAsyncThunk('wallet/getCoins', async (mode) => {
    const url = `/wallet/${mode}/coins`;
    const response = await axiosInstance({
        method: 'GET',
        url,
    });
    return response.data;
});

export const refreshCoin = createAsyncThunk('coins/refreshCoin', async ({ coinId, mode }) => {
    const url = `/wallet/${mode}/coins/${coinId}/refresh`;
    const response = await axiosInstance({
        method: 'POST',
        url,
    });
    return { coinId, coinData: response.data.data };
});

export const getSingleCoin = createAsyncThunk('coins/getSingleCoin', async ({ coinId, chainId, mode }) => {
    const url = `/wallet/${mode}/coins/${coinId}`;
    const response = await axiosInstance({
        method: 'GET',
        url,
        params: { chainId }
    });
    return { coinId, chainId, coinData: response.data.data };
});

const initialState = {
    chains: [],
    loading: true,
    coin: null,
    coinHistory: [],
    refreshingCoins: {}, // Track which coins are being refreshed
    singleCoinLoading: false, // Track single coin fetch loading
};

const coinsSlice = createSlice({
    name: 'coins',
    initialState,
    reducers: {
        setloading: (state, action) => {
            state.loading = true;
        },
        updateCoinData: (state, action) => {
            const { coinId, coinData } = action.payload;
            // Find and update the specific coin in chains
            state.chains.forEach(chain => {
                const coinIndex = chain.coins.findIndex(coin => coin.id === coinId);
                if (coinIndex !== -1) {
                    chain.coins[coinIndex] = { ...chain.coins[coinIndex], ...coinData };
                }
            });
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCoins.pending, (state) => {
                state.loading = true;
            })
            .addCase(getCoins.fulfilled, (state, action) => {
                state.chains = action.payload.data;
                state.loading = false;
            })
            .addCase(getCoins.rejected, (state, action) => {
                state.loading = false;
                console.error('Failed to fetch coins:', action.error);
            })
            // Individual coin refresh cases
            .addCase(refreshCoin.pending, (state, action) => {
                const { coinId } = action.meta.arg;
                console.log('Setting refreshing state for coin:', coinId);
                state.refreshingCoins[coinId] = true;
                console.log('Updated refreshingCoins:', state.refreshingCoins);
            })
            .addCase(refreshCoin.fulfilled, (state, action) => {
                const { coinId, coinData } = action.payload;
                // Update the specific coin data
                state.chains.forEach(chain => {
                    const coinIndex = chain.coins.findIndex(coin => coin.id === coinId);
                    if (coinIndex !== -1) {
                        chain.coins[coinIndex] = { ...chain.coins[coinIndex], ...coinData };
                    }
                });
                // Remove from refreshing state
                delete state.refreshingCoins[coinId];
            })
            .addCase(refreshCoin.rejected, (state, action) => {
                const { coinId } = action.meta.arg;
                delete state.refreshingCoins[coinId];
                console.error('Failed to refresh coin:', action.error);
            })
            // Single coin fetch cases
            .addCase(getSingleCoin.pending, (state) => {
                state.singleCoinLoading = true;
            })
            .addCase(getSingleCoin.fulfilled, (state, action) => {
                const { coinId, chainId, coinData } = action.payload;
                state.singleCoinLoading = false;
                
                // Find or create the chain
                let targetChain = state.chains.find(chain => String(chain.id) === String(chainId));
                if (!targetChain) {
                    // If chain doesn't exist, create it
                    targetChain = {
                        id: chainId,
                        name: coinData.chain || 'Unknown Chain',
                        coins: []
                    };
                    state.chains.push(targetChain);
                }
                
                // Find or create the coin in the chain
                const coinIndex = targetChain.coins.findIndex(coin => String(coin.id) === String(coinId));
                if (coinIndex !== -1) {
                    // Update existing coin
                    targetChain.coins[coinIndex] = { ...targetChain.coins[coinIndex], ...coinData };
                } else {
                    // Add new coin to chain
                    targetChain.coins.push({ id: coinId, ...coinData });
                }
            })
            .addCase(getSingleCoin.rejected, (state, action) => {
                state.singleCoinLoading = false;
                console.error('Failed to fetch single coin:', action.error);
            });
    }
});

export const { setloading, updateCoinData } = coinsSlice.actions;

export default coinsSlice.reducer;